/**
 * Sprint 1069.C — Universal Flow Breadcrumb.
 *
 * Drop-in stage breadcrumb that auto-detects (or accepts) the active mission
 * and stage based on the current pathname. Generalizes the old
 * `BrandJourneyMap` — same look-and-feel, but driven by `/api/loop/missions`
 * so all 5 missions (Sales, Marketing, Projects, Finance, Ops) share one
 * breadcrumb pattern.
 *
 * Props:
 *   mission?  — mission id (e.g. "win-the-client"). If omitted, auto-detected
 *               from pathname via PATH_TO_MISSION_STAGE.
 *   stage?    — current stage id. If omitted, auto-detected likewise.
 *   compact?  — render the small variant (default true for top-of-page injection).
 *
 * Click any stage chip → navigate to its `page`. Milestone-only stages
 * (Win, Paid) are rendered as emerald check chips and are non-navigable.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import StaleRowsDrawer from "./StaleRowsDrawer";
// Mission Architecture Alignment (2026-06-03) — short-circuit the synthetic
// Brand mission so we never hit /api/loop/missions/build-and-protect-the-brand.
import { getBrandMissionDetail, isBrandStudioPath } from "../../config/missionRegistry";

/**
 * Pathname (or pathname prefix) → { mission, stage }.
 * Longest-match-wins ordering. Add new pages here to wire the breadcrumb.
 */
const PATH_TO_MISSION_STAGE = [
  // ── Win the Client
  ["/sales-studio",          { mission: "win-the-client", stage: "lead" }],
  ["/concept-room",          { mission: "win-the-client", stage: "concept" }],
  ["/leads",                 { mission: "win-the-client", stage: "lead" }],
  ["/contacts",              { mission: "win-the-client", stage: "lead" }],
  ["/briefs",                { mission: "win-the-client", stage: "concept" }],
  ["/kanban",                { mission: "win-the-client", stage: "lead" }],
  ["/quotes",                { mission: "win-the-client", stage: "quote" }],
  ["/forms",                 { mission: "win-the-client", stage: "quote" }],
  ["/sales-calendar",        { mission: "win-the-client", stage: "lead" }],
  ["/client-health",         { mission: "win-the-client", stage: "lead" }],
  ["/administration/health", { mission: "win-the-client", stage: "lead" }],
  ["/proposal-builder",      { mission: "win-the-client", stage: "proposal" }],
  ["/one-page-pitches",      { mission: "win-the-client", stage: "pitch" }],
  ["/pitch-decks",           { mission: "win-the-client", stage: "pitch" }],
  // Sprint 2026-06-12 PM-10.6 — /calendar and /analytics are app-wide meta-views,
  // NOT Sales-mission rows. Removed from PATH_TO_MISSION_STAGE so the universal
  // FlowBreadcrumb does not claim them for "win-the-client".

  // ── Grow the Brand (magic arc)
  ["/web-studio",                { mission: "grow-the-brand", stage: "build" }],
  ["/marketing/website",         { mission: "grow-the-brand", stage: "build" }],
  ["/marketing-studio",          { mission: "grow-the-brand", stage: "diagnose" }],
  ["/marketing/brand-setup",     { mission: "grow-the-brand", stage: "build" }],
  ["/marketing/diagnostic",      { mission: "grow-the-brand", stage: "diagnose" }],
  ["/marketing/ai-positioning",  { mission: "grow-the-brand", stage: "position" }],
  ["/marketing/brand-dashboard", { mission: "grow-the-brand", stage: "improve" }],
  ["/marketing/plan",            { mission: "grow-the-brand", stage: "plan" }],
  ["/marketing/plans",           { mission: "grow-the-brand", stage: "plan" }],
  ["/marketing/roi",             { mission: "grow-the-brand", stage: "measure" }],
  ["/marketing/social-hub",      { mission: "grow-the-brand", stage: "publish" }],
  ["/marketing/dashboard",       { mission: "grow-the-brand", stage: "improve" }],
  ["/creative-studio",           { mission: "grow-the-brand", stage: "create" }],
  // Sprint 1070.C5-prep6 — Web Studio canonical destinations (Integrate / Widgets /
  // Funnel / Migrate). All belong to the "build" stage of Grow the Brand so the
  // parent FlowBreadcrumb persists when the tenant deeplinks via Web Studio sidebar.
  ["/platform/external-site",    { mission: "grow-the-brand", stage: "build" }],

  // ── Run the Project
  // ── Run the Project
  ["/projects-studio",           { mission: "run-the-project", stage: "production" }],
  ["/projects",                  { mission: "run-the-project", stage: "production" }],
  ["/deliveries",                { mission: "run-the-project", stage: "deliver" }],
  ["/crew",                      { mission: "run-the-project", stage: "production" }],

  // ── Get Paid
  ["/admin/invoices",            { mission: "get-paid", stage: "invoice" }],
  ["/invoices",                  { mission: "get-paid", stage: "invoice" }],
  ["/profitability",             { mission: "get-paid", stage: "paid" }],

  // ── Build & Protect the Brand (Brand Studio) — Mission Architecture Alignment 2026-06-03
  // Sprint 2026-06-05 PM-7 — Brand Studio pages now belong contextually to the
  // Grow-the-Brand mission for row 2 of the canonical breadcrumb stack. The
  // Brand pillars themselves are row 1 (BrandStudioPillarsBreadcrumb).
  ["/brand-studio/foundation",        { mission: "grow-the-brand", stage: "build" }],
  ["/brand-studio/protect",           { mission: "grow-the-brand", stage: "build" }],
  ["/brand-studio/strengthen",        { mission: "grow-the-brand", stage: "build" }],
  ["/brand-studio/brand-health",      { mission: "grow-the-brand", stage: "improve" }],
  ["/brand-studio/brand-performance", { mission: "grow-the-brand", stage: "improve" }],
  ["/brand-studio/steward",           { mission: "grow-the-brand", stage: "improve" }],
  ["/brand-studio",                   { mission: "grow-the-brand", stage: "build" }],
];

// Full-screen editor routes: the universal breadcrumb is suppressed because
// they have their own chrome and the breadcrumb fights for space.
const EDITOR_PATTERNS = [
  /^\/proposal-builder\/[^/]+/,  // /proposal-builder/{id}
  /^\/pitch-decks\/[^/]+/,
  /^\/one-page-pitches\/[^/]+/,
  /^\/concept-room\/[^/]+/,
];

export function isEditorRoute(pathname) {
  return EDITOR_PATTERNS.some((re) => re.test(pathname));
}

export function pathToMissionStage(pathname) {
  const match = PATH_TO_MISSION_STAGE
    .filter(([prefix]) => pathname.startsWith(prefix))
    .sort((a, b) => b[0].length - a[0].length)[0];
  return match ? match[1] : null;
}

// Tiny module-level cache so all FlowBreadcrumb instances share fetches.
const _cache = {};

async function fetchMissionDetail(missionId) {
  if (_cache[missionId]) return _cache[missionId];
  // Mission Architecture Alignment (2026-06-03) — synthetic Brand mission.
  if (missionId === "build-and-protect-the-brand") {
    _cache[missionId] = getBrandMissionDetail();
    return _cache[missionId];
  }
  const r = await api.get(`/loop/missions/${missionId}`);
  _cache[missionId] = r.data;
  return r.data;
}

export default function FlowBreadcrumb({ mission: missionProp, stage: stageProp, compact = true }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { language, t } = useLanguage();

  const detected = useMemo(() => pathToMissionStage(pathname), [pathname]);
  const missionId = missionProp || detected?.mission;
  const stageId = stageProp || detected?.stage;
  // Sprint 1069.D2 — suppress on full-screen editor pages.
  // Sprint 2026-06-05 PM-7 — Brand Studio pages now show the canonical
  // 3-row breadcrumb stack: (row 1) BrandStudioPillarsBreadcrumb,
  // (row 2) this FlowBreadcrumb mounted with the Grow-the-Brand mission,
  // (row 3) BrandStudioBreadcrumb path crumb. Previously this component
  // self-suppressed on `/brand-studio/*` via `isBrandStudioPath` which
  // killed row 2. The suppression is now gated by editor routes only.
  //
  // Sprint 2026-06-11 PM-8 Phase 1 (paco) — on the canonical Brand pages
  // we render the Mission row via BrandStudioPillarsBreadcrumb (which
  // shows "MISSION: BUILD & PROTECT THE BRAND" + pillar children).
  // FlowBreadcrumb here would render "Grow the Brand" — the WRONG
  // mission for Brand Studio. Suppress on Phase-1 paths only.
  const BRAND_CANONICAL_PHASE1_PATHS = [
    "/brand-studio/foundation/setup",
    "/brand-studio/foundation/identity",
    "/brand-studio/foundation/voice",
    "/brand-studio/foundation/repository",
    "/brand-studio/foundation/launch",
    "/brand-studio/protect/identity-analysis",
    "/brand-studio/protect/domain-portfolio",
    "/brand-studio/protect/trademark-readiness",
    "/brand-studio/protect/evidence-center",
    "/brand-studio/strengthen/diagnostic",
    "/brand-studio/strengthen/positioning",
    "/brand-studio/strengthen/audience",
    "/brand-studio/strengthen/competitor",
    "/brand-studio/brand-health",
    "/brand-studio/brand-performance",
    "/brand-studio/steward/living-bi",
  ];
  // Sprint 2026-06-11 PM-9 Phase 3C → 2026-06-12 PM-10.4 Phase 3D — every
  // Sales leaf route now renders the canonical 2-row breadcrumb;
  // suppress the legacy mission row on all of them.
  // Sprint 2026-06-12 PM-10.7 — Sales canonical Phase-1 paths (suppress legacy
  // mission row on these). Mirrors PHASE1_CANONICAL_PATHS in
  // SalesStudioPillarsBreadcrumb.jsx. Includes all Sales-cluster destinations
  // (calendar/analytics now ARE Sales surfaces per paco UX decision).
  // Prefix-aware so deep routes (e.g. /leads/abc) also suppress.
  const SALES_CANONICAL_PHASE1_PATHS = [
    "/sales-studio",
    "/leads",
    "/contacts",
    "/briefs",
    "/concept-room",
    "/quotes",
    "/forms",
    "/intake-forms",
    "/proposal-builder",
    "/proposal-analytics",
    "/one-page-pitches",
    "/kanban",
    "/administration/health",
    "/calendar",
    "/meetings",
    "/analytics",
  ];
  const matchesSalesCanonical = (p) =>
    SALES_CANONICAL_PHASE1_PATHS.some(
      (prefix) => p === prefix || p.startsWith(prefix + "/")
    );
  // Phase 1 · Marketing breadcrumb rollout (paco-approved). Suppress the legacy
  // mission row on the Marketing canonical leaves so only the 2-row canonical
  // breadcrumb renders (no forbidden 3rd row). Mirrors MARKETING_CANONICAL_PATHS.
  const MARKETING_CANONICAL_PHASE1_PATHS = [
    "/marketing/research",
    "/marketing/seo-analyzer",
    "/marketing/plan",
    "/marketing/plans",
    "/marketing/social-batch",
    "/marketing/email-sequence",
    "/marketing/landing",
    "/marketing/brand-launch",
    "/marketing/roi",
    "/marketing/dashboard",
  ];
  // CANON C4 (2026-07-08): one mission locator system only = MissionCommandBar.
  // Retire the legacy FlowBreadcrumb across the Marketing-area hubs; each keeps its
  // own in-page Studio shell. Exact-match roots only.
  const LEGACY_HUB_SUPPRESS_PATHS = [
    "/marketing-studio",
    "/marketing",
    "/creative-studio",
    "/web-studio",
  ];
  const suppress = isEditorRoute(pathname)
    || BRAND_CANONICAL_PHASE1_PATHS.includes(pathname)
    || matchesSalesCanonical(pathname)
    || MARKETING_CANONICAL_PHASE1_PATHS.includes(pathname)
    || LEGACY_HUB_SUPPRESS_PATHS.includes(pathname);

  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState({});
  const [openDrawerStage, setOpenDrawerStage] = useState(null);
  const countRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    if (!missionId) { setDetail(null); return; }
    fetchMissionDetail(missionId)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setDetail(null); });
    return () => { cancelled = true; };
  }, [missionId]);

  // Sprint 1069.D — fetch per-stage live-work counts.
  useEffect(() => {
    let cancelled = false;
    if (!missionId) { setProgress({}); return; }
    // Mission Architecture Alignment (2026-06-03) — Brand is frontend-only;
    // backend has no progress endpoint for it. Default to {}.
    if (missionId === "build-and-protect-the-brand") {
      setProgress({});
      return;
    }
    api.get(`/loop/mission-progress?mission=${missionId}`)
      .then(r => { if (!cancelled) setProgress(r.data?.counts || {}); })
      .catch(() => { if (!cancelled) setProgress({}); });
    return () => { cancelled = true; };
  }, [missionId, pathname]);

  if (!missionId || !detail || suppress) return null;
  const stages = detail.stages || [];
  if (stages.length === 0) return null;

  const missionLabel = detail.mission?.label?.[language] || detail.mission?.label?.en || missionId;
  const currentIdx = stages.findIndex(s => s.id === stageId);

  // Sprint 1070.C5-prep5 — Quest-log hint. Find the FIRST clickable
  // stage with zero progress so we can highlight it as "Start here →".
  // Only one stage per mission gets the hint — the next high-leverage
  // empty step. We deliberately exclude milestones (Win, Paid) and the
  // current stage (already highlighted differently). This turns the
  // breadcrumb into a guided journey indicator.
  const startHereStageId = (() => {
    for (const s of stages) {
      if (s.is_milestone_only || !s.page) continue;
      if (s.id === stageId) continue;
      if ((progress[s.id] || 0) > 0) continue;
      return s.id;
    }
    return null;
  })();

  return (
    <div className="w-full mb-4" data-testid="flow-breadcrumb" data-mission={missionId} data-stage={stageId || ""}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)]">
          {t("Mission")}:
        </span>
        <span className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: detail.mission?.color || "var(--app-accent)" }}
          data-testid="flow-breadcrumb-mission-label">
          {missionLabel}
        </span>
      </div>
      <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin" data-testid="flow-breadcrumb-stages">
        {stages.map((s, i) => {
          const stageLabel = s.label?.[language] || s.label?.en || s.id;
          const isCurrent = i === currentIdx;
          const isPast = currentIdx >= 0 && i < currentIdx;
          const isFuture = currentIdx >= 0 && i > currentIdx;
          const clickable = !!s.page && !s.is_milestone_only;
          const accent = detail.mission?.color || "var(--app-accent)";
          const isStartHere = s.id === startHereStageId;

          return (
            <div key={s.id} className="flex items-center shrink-0">
              <button
                onClick={() => clickable && navigate(s.page)}
                disabled={!clickable}
                className={`flex items-center gap-1.5 rounded-lg ${compact ? "px-2.5 py-1" : "px-3 py-2"} text-xs font-medium border transition-all
                  ${isCurrent
                    ? "shadow-sm"
                    : isStartHere
                      ? "border-amber-400/60 bg-amber-400/10 text-amber-300 hover:bg-amber-400/15 ring-1 ring-amber-400/30 animate-pulse-soft"
                      : isPast
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                        : isFuture
                          ? "border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text-muted)] opacity-70 hover:opacity-100"
                          : "border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text)]"
                  }
                  ${clickable ? "cursor-pointer hover:scale-[1.02]" : "cursor-default"}
                `}
                style={isCurrent ? { borderColor: `${accent}55`, backgroundColor: `${accent}15`, color: accent } : undefined}
                data-testid={`flow-breadcrumb-stage-${s.id}`}
                data-current={isCurrent}
                data-start-here={isStartHere || undefined}
                data-clickable={clickable}
                title={isStartHere ? t("Start here — no work tracked yet at this stage") : undefined}
              >
                <span className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px] font-bold shrink-0"
                  style={{
                    backgroundColor: isCurrent
                      ? `${accent}30`
                      : isStartHere ? "rgba(251,191,36,0.25)" : isPast ? "rgba(16,185,129,0.25)" : "var(--app-border)",
                    color: isCurrent
                      ? accent
                      : isStartHere ? "rgb(252,211,77)" : isPast ? "rgb(110,231,183)" : "var(--app-text-muted)",
                  }}>
                  {s.is_milestone_only ? <CheckCircle2 className="w-2.5 h-2.5" /> : i + 1}
                </span>
                {stageLabel}
                {/* Sprint 1070.C5-prep5 — quest-log hint label. */}
                {isStartHere && (
                  <span
                    className="ml-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40"
                    data-testid={`flow-breadcrumb-start-here-${s.id}`}
                  >
                    {t("Start here →")}
                  </span>
                )}
                {/* Sprint 1069.D — live-work counter pill. */}
                {progress[s.id] > 0 && (
                  <span
                    ref={(el) => { countRefs.current[s.id] = el; }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDrawerStage(openDrawerStage === s.id ? null : s.id);
                    }}
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                    style={{
                      backgroundColor: isCurrent ? `${accent}` : "var(--app-text-muted)",
                      color: "var(--app-bg)",
                    }}
                    data-testid={`flow-breadcrumb-count-${s.id}`}
                    data-count={progress[s.id]}
                    title="Click to see oldest at this stage"
                  >
                    {progress[s.id]}
                  </span>
                )}
              </button>
              {/* Sprint 1069.D2 — stale-rows drawer for this chip (portal'd to body). */}
              {openDrawerStage === s.id && progress[s.id] > 0 && (
                <StaleRowsDrawer
                  mission={missionId}
                  stage={s.id}
                  count={progress[s.id]}
                  anchorRef={{ current: countRefs.current[s.id] }}
                  onClose={() => setOpenDrawerStage(null)}
                />
              )}
              {i < stages.length - 1 && (
                <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
